import { setDifference } from "csi/utility/arrayUtility";
import { action, computed, makeObservable, observable } from "mobx";

export type DataStoreSelectionCallback = (id: string, removed: boolean) => void;

export interface DataStoreSelectionOptions {
	/** indicates if existing selections can be removed */
	readonly allowRemove?: boolean;

	/** indicates if multiple selections are allowed */
	readonly multiple?: boolean;
}

class DataStoreSelectionStore {
	private readonly _initialValue = observable.set<string>();
	private readonly _value = observable.set<string>();

	/** callback to be notified of added or removed selection */
	readonly onSelect?: DataStoreSelectionCallback;

	/** indicates if existing selections can be removed */
	readonly options?: DataStoreSelectionOptions;

	/** ids that have been newly selected from the initial value */
	@computed get addedSelections(): string[] {
		const { _initialValue, _value } = this;
		return setDifference(_value, _initialValue);
	}

	/** indicates if existing selections (initial value) can be unselected */
	get allowRemove(): boolean {
		return !!this.options?.allowRemove;
	}

	/** indicates if selections have changed from initial value */
	@computed get hasChanged(): boolean {
		const { addedSelections, removedSelections } = this;
		return !!addedSelections.length || !!removedSelections.length;
	}

	/** indicates if there are selections to unselect */
	get hasSelections(): boolean {
		return !!this.addedSelections.length;
	}

	/** indicates if multiple selections are allowed */
	get multiple(): boolean {
		return !!this.options?.multiple;
	}

	/** ids that have been unselected from the initial value */
	@computed get removedSelections(): string[] {
		const { _initialValue, _value } = this;
		return setDifference(_initialValue, _value);
	}

	/** return true if id cannot be unselected */
	isSelectionDisabled = (id: string): boolean => {
		const { allowRemove, _initialValue } = this;
		return _initialValue.has(id) && !allowRemove;
	};

	/** return true if id is selected */
	isSelected = (id: string): boolean => {
		return this._value.has(id);
	};

	/** add a selection */
	@action addSelection = (id: string): void => {
		const { multiple, onSelect, _value } = this;

		if (!multiple) {
			// remove existing selection if only 1 selection allowed
			if (onSelect) {
				for (const x of _value) {
					onSelect(x, true);
				}
			}

			_value.clear();
		}

		// add selection
		_value.add(id);
		onSelect?.(id, false);
	};

	/** clear all selections */
	@action clearSelections = (): void => {
		const { addedSelections, removeSelection } = this;

		for (const id of addedSelections) {
			removeSelection(id);
		}
	};

	/** remove a selection */
	@action removeSelection = (id: string): void => {
		this._value.delete(id);
		this.onSelect?.(id, true);
	};

	/** reset selections to the initial value */
	@action resetSelections = (): void => {
		const { _initialValue, _value } = this;

		_value.clear();

		for (const id of _initialValue) {
			this._value.add(id);
		}
	};

	constructor(options?: DataStoreSelectionOptions, value?: string[], onSelect?: DataStoreSelectionCallback) {
		makeObservable(this);
		this.onSelect = onSelect;
		this.options = options;

		if (value) {
			for (const id of value) {
				this._initialValue.add(id);
				this._value.add(id);
			}
		}
	}
}

export default DataStoreSelectionStore;
